import { noop } from 'lodash';
import { createContext, memo, type ReactNode, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PDFSearchBridgeContextType = {
  searchState: {
    totalResults: number;
    isSearching: boolean;
    currentResultIndex: number;
  };
  initialSearchPhrase: string | null;
  triggerSearch: (phrase: string) => void;
  triggerClearSearch: () => void;
  setCurrentResult: (index: number) => void;
  setInitialSearchPhrase: (phrase: string | null) => void;
  // Internal methods for PDFSearchWrapper to update state
  updateSearchState: (state: { totalResults: number; isSearching: boolean; currentResultIndex: number }) => void;
  setSearchFunctions: (functions: {
    onSearchPhraseChange: (phrase: string) => void;
    clearSearch: () => void;
    setCurrentResultIndex: (index: number) => void;
  }) => void;
};

const defaultValue: PDFSearchBridgeContextType = {
  searchState: {
    totalResults: 0,
    isSearching: false,
    currentResultIndex: -1,
  },
  initialSearchPhrase: null,
  triggerSearch: noop,
  triggerClearSearch: noop,
  setCurrentResult: noop,
  setInitialSearchPhrase: noop,
  updateSearchState: noop,
  setSearchFunctions: noop,
};

const PDFSearchBridgeContext = createContext<PDFSearchBridgeContextType>(defaultValue);

type PDFSearchBridgeContextProviderProps = {
  children: ReactNode;
};

export const PDFSearchBridgeContextProvider = memo(({ children }: PDFSearchBridgeContextProviderProps) => {
  const [searchState, setSearchState] = useState(defaultValue.searchState);
  const [initialSearchPhrase, setInitialSearchPhrase] = useState<string | null>(null);
  const [searchFunctions, setSearchFunctions] = useState<{
    onSearchPhraseChange?: (phrase: string) => void;
    clearSearch?: () => void;
    setCurrentResultIndex?: (index: number) => void;
  }>({});

  const value = useMemo<PDFSearchBridgeContextType>(
    () => ({
      searchState,
      initialSearchPhrase,
      triggerSearch: (phrase: string) => searchFunctions.onSearchPhraseChange?.(phrase),
      triggerClearSearch: () => searchFunctions.clearSearch?.(),
      setCurrentResult: (index: number) => searchFunctions.setCurrentResultIndex?.(index),
      setInitialSearchPhrase,
      updateSearchState: setSearchState,
      setSearchFunctions,
    }),
    [searchState, initialSearchPhrase, searchFunctions],
  );

  return <PDFSearchBridgeContext.Provider value={value}>{children}</PDFSearchBridgeContext.Provider>;
});

PDFSearchBridgeContextProvider.displayName = 'PDFSearchBridgeContextProvider';

export const usePDFSearchBridgeContext = () => {
  const context = useContext(PDFSearchBridgeContext);
  const error = new Error('usePDFSearchBridgeContext must be used within a PDFSearchBridgeContextProvider');

  if (context === defaultValue) {
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error.message,
      });
    }
  }

  return context;
};
